import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import API_ENDPOINTS from '../ApiEndPoints';

const ContentPageEditor = ({ id, ...otherProps }) => {
    const [page, setPage] = useState(null);
    const [html, setHtml] = useState('');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [showPreview, setShowPreview] = useState(true);

    useEffect(() => {
        loadData();
    }, [id]);

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_ENDPOINTS.webpages.get(id));
            setPage(response.data);
            setHtml(response.data.html || '');
        } catch (err) {
            console.error("Error loading page:", err);
            setError("Failed to load page data.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await axios.post(API_ENDPOINTS.webpages.save(id), {
                html: html
            });
            // Show success message (maybe a toast)
            alert('Page saved successfully!');
        } catch (err) {
            console.error("Error saving page:", err);
            alert('Failed to save page.');
        } finally {
            setSaving(false);
        }
    };

    const handleHtmlChange = (e) => {
        setHtml(e.target.value);
    };

    if (loading) {
        return <div className="flex items-center justify-center h-screen">Loading...</div>;
    }

    if (error) {
        return <div className="text-red-500 p-4">{error}</div>;
    }

    return (
        <div className="flex flex-col h-screen bg-gray-100">
            {/* Header */}
            <div className="bg-white border-b px-4 py-3 flex justify-between items-center shadow-sm">
                <div>
                    <h1 className="text-lg font-semibold text-gray-800">{page?.title || 'Untitled Page'}</h1>
                    <p className="text-sm text-gray-500">ID: {id}</p>
                </div>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setShowPreview(!showPreview)}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        {showPreview ? 'Hide Preview' : 'Show Preview'}
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-4 py-1.5 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                        onClick={() => window.location.href = `/webpages/edit_cb/${id}`}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Old Editor
                    </button>
                    <button
                        onClick={() => window.location.href = '/webpages'}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Close
                    </button>
                </div>
            </div>

            {/* Main Content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Editor Pane */}
                <div className={`flex-1 flex flex-col border-r ${showPreview ? 'w-1/2' : 'w-full'}`}>
                    <div className="bg-gray-50 px-4 py-2 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
                        HTML Editor
                    </div>
                    <textarea
                        className="flex-1 w-full p-4 font-mono text-sm resize-none focus:outline-none"
                        value={html}
                        onChange={handleHtmlChange}
                        placeholder="Enter HTML content here..."
                    />
                </div>

                {/* Preview Pane */}
                {showPreview && (
                    <div className="flex-1 flex flex-col w-1/2 bg-white">
                        <div className="bg-gray-50 px-4 py-2 border-b text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Preview
                        </div>
                        <div className="flex-1 overflow-auto p-4">
                            <iframe
                                title="Preview"
                                srcDoc={html}
                                className="w-full h-full border-0"
                                sandbox="allow-scripts"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ContentPageEditor;
